import { Heading } from "@/components/Heading/Heading";
import { type IconId, PixelIcon } from "@/components/PixelIcon/PixelIcon";
import Spinner from "@/images/spinner.svg";
import { useId, useState } from "react";
import { IoMdClose as IconClose } from "react-icons/io";
import css from "./Callout.module.scss";

type Props = {
  variant: Variant;
  icon?: IconId;
  title?: {
    level: React.ComponentProps<typeof Heading>["level"];
    text: string;
  };
  /**
   * Show the callout partially collapsed by default with an expand button at
   * the bottom.
   */
  isExpandable?: boolean;
  /**
   * Show a spinner animation.
   */
  active?: boolean;
  children: React.ReactNode;
  onClose?: () => void;
};

type Variant = "yellow" | "green" | "red" | "blue" | "overlay";

export function Callout({ variant, icon, title, isExpandable = false, active = false, children, onClose }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const bodyId = useId();

  return (
    <div
      className={css.callout}
      data-variant={variant}
      data-has-icon={icon != null}
      data-has-close-button={onClose ? true : undefined}
      data-is-active={active}
    >
      {active && <Spinner className={css.spinner} aria-hidden={true} />}

      {icon != null && <PixelIcon className={css.icon} icon={icon} size={24} />}

      {onClose && (
        <button className={css.closeButton} aria-label="Lukk" onClick={onClose}>
          <IconClose />
        </button>
      )}

      <div className={css.content}>
        {title && (
          <Heading className={css.title} level={title.level} display={2}>
            {title.text}
          </Heading>
        )}

        {isExpandable && (
          <button
            className={css.expandButton}
            aria-expanded={isExpanded}
            aria-controls={bodyId}
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? "Vis mindre" : "Vis mer"}
          </button>
        )}

        <div id={bodyId} className={css.body} aria-hidden={isExpandable ? isExpanded === false : undefined}>
          {children}
        </div>
      </div>
    </div>
  );
}
